import React, { memo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { TableState } from '../../features/DataUtil';
import { RaceData } from '../../features/RaceDataUtil';
import { HistoryTableData, HistoryTableTitle } from '../../features/ViewModel';
import { I18n, LABEL_CATEGORY, useI18n } from '../../hooks/I18n';
import { useData } from '../../hooks/UseData';
import { defined } from '../../utils/Util';
import { HistoryItems, TableBackDrop } from './StyledComponents';
import { TableBaseProps } from './TableBase';

type TableProps = TableBaseProps<HistoryTableData> & {
  initialState: TableState;
  onStateChanged: (newState: TableState) => void;
};

//  5個より多い場合はフィルタする
const FILTER_THRESHOLD = 5;

const createElement = (
  tableData: HistoryTableData,
  titles: HistoryTableTitle[],
  prefix: string,
  postfix: string,
  first: boolean,
  category: string,
  i18n: I18n,
  raceData: RaceData
): React.ReactElement => {
  const message =
    prefix +
    (first ? tableData.localizeCategory(category, i18n, raceData) : '') +
    titles
      .map((title) => tableData.localizeTitle(title, i18n))
      .join(i18n.LABELS.localize('separator_title', LABEL_CATEGORY.TITLE)) +
    postfix;
  return (
    <Box component="span" key={message}>
      {message}
    </Box>
  );
};

const createViewData = (
  tableData: HistoryTableData,
  i18n: I18n,
  raceData: RaceData
): [React.ReactElement, React.ReactElement[]][] =>
  Object.entries(tableData.dataMap).map(([year, categoriesData]) => {
    const elementsArray: React.ReactElement[] = [];
    const yearElement: React.ReactElement = (
      <>{tableData.localizeYear(year, i18n, raceData)}</>
    );
    for (const [category, roundsData] of Object.entries(categoriesData)) {
      const elements = [];
      let first = true;

      const createTitleElement = (
        titles: HistoryTableTitle[],
        prefix: string,
        postfix: string
      ) =>
        createElement(
          tableData,
          titles,
          prefix,
          postfix,
          first,
          category,
          i18n,
          raceData
        );

      // 初参戦
      if (roundsData.firstData.length > 0) {
        elements.push(
          createTitleElement(
            roundsData.firstData,
            '',
            i18n.LABELS.localize('postfix_entry', LABEL_CATEGORY.TITLE)
          )
        );
        first = false;
      }
      for (const [round, titles] of Object.entries(roundsData.roundData)) {
        const prefix =
          tableData.localizeRaceId(round, i18n, raceData) +
          i18n.LABELS.localize('round_at', LABEL_CATEGORY.TITLE);
        elements.push(
          createTitleElement(
            titles,
            prefix,
            i18n.LABELS.localize('postfix_title', LABEL_CATEGORY.TITLE)
          )
        );
        first = false;
      }
      // 年間優勝
      if (roundsData.lastData.length > 0) {
        const localizedCategory = tableData.localizeCategory(
          category,
          i18n,
          raceData
        );
        // カテゴリが存在する場合のみ round_at を付与
        const prefix =
          localizedCategory !== ''
            ? localizedCategory +
              i18n.LABELS.localize('round_at', LABEL_CATEGORY.TITLE)
            : '';
        elements.push(
          createTitleElement(
            roundsData.lastData,
            prefix,
            i18n.LABELS.localize('postfix_champion', LABEL_CATEGORY.TITLE)
          )
        );
        first = false;
      }
      elementsArray.push(<>{elements}</>);
    }
    return [yearElement, elementsArray];
  });

const HistoryTable: React.FC<TableProps> = ({
  isLoading,
  tableData,
  initialState,
  onStateChanged,
  onReadyToScroll,
}) => {
  const i18n = useI18n();
  const { fetchRaceData } = useData();
  const raceData = fetchRaceData();
  const [state, setState] = useState({
    showAll: initialState.showAll ?? false,
  });

  const viewData = React.useMemo(
    () => createViewData(tableData, i18n, raceData),
    [tableData]
  );

  let readMore = undefined;
  let visibleCount = viewData.length;
  if (!state.showAll) {
    visibleCount = FILTER_THRESHOLD;
    const isFiltered = viewData.length > FILTER_THRESHOLD;
    if (isFiltered) {
      readMore = (
        <Typography variant="body2" sx={{ px: 2 }}>
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              setState({ showAll: true });
              onStateChanged({ showAll: true });
            }}
          >
            {i18n.LABELS.localize('show_all', LABEL_CATEGORY.TITLE)}
          </a>
        </Typography>
      );
    }
  }

  React.useEffect(() => {
    // ページ全体のスクロールのため通知する
    if (defined(onReadyToScroll)) {
      onReadyToScroll();
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex', // 横幅はコンテンツに合わせる
          flexDirection: 'row', // 横方向に並べる
          flexWrap: 'wrap', // はみ出したら改行
          width: '100%', // 親の幅と合わせる
        }}
      >
        <TableBackDrop open={isLoading} />
        {React.useMemo(
          () => (
            <HistoryItems items={viewData} visibleCount={visibleCount} />
          ),
          [viewData, visibleCount]
        )}
      </Box>
      {readMore}
    </>
  );
};

export default memo(HistoryTable);
